import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import { Hero, PERSONALITY } from 'componentsV2/Hero';
import Breadcrumbs from 'components/Breadcrumbs';
import { setHelmetTags } from 'helpers/HelmetHelper';
import Schema from 'componentsV2/Schema';
import { SCHEMA_TYPES } from 'componentsV2/Schema/SchemaHelper';
import Bio from './components/Bio';
import Faq from './components/Faq';
import ShowPanel from './components/ShowPanel';
import FetchContent from './Hooks';
import { fetchCategoryDetail } from './PersonalityActions';
import { getBreadcumbsData, getSocialLinksFromIds } from './PersonalityHelper';

import Style from './Personality.module.scss';

const Personality = ({ accessToken, bookmarks, deviceId, favoritesManager, params, ...rest }) => {
  const showId = params?.showId;
  FetchContent({ accessToken, deviceId, showId });
  // favoritableItems is needed for Favorite Button under Hero, collections is used for Shows carousel
  const { seo, faq, seriesData, bio, socials, collections } = useSelector(
    state => state.personality,
  );
  const socialLinks = getSocialLinksFromIds({
    facebookId: socials?.facebook,
    instagramId: socials?.instagram,
    twitterId: socials?.twitter,
  });
  const faqSchema = faq ? { type: SCHEMA_TYPES.FAQ, item: faq } : {};
  const personSchema = seriesData
    ? { item: { ...seriesData, social: socialLinks }, type: SCHEMA_TYPES.PERSON }
    : {};
  const schemas = [];
  if (seriesData) schemas.push(personSchema);
  if (faq) schemas.push(faqSchema);
  useEffect(() => {
    const { categoryType, headline } = seriesData ?? {};
    if (categoryType && headline) {
      handleAnalytics({
        event: Events.DETAIL_PAGE_DEFAULT_STATE,
        payload: {
          pageName: `delta:web:detail:personality:${headline}`,
          pageType: `detail:personality:landing`,
          pageCollectionTitle: window.DataLayer.referringCollection ?? 'no collection title',
          pageCollectionType: window.DataLayer.referringCollection
            ? 'general'
            : 'no collection type',
        },
      });
    }
  }, [seriesData]);
  return (
    <main className={Style.container}>
      {setHelmetTags(seo)}
      <Schema schemas={schemas} />
      <Hero item={seriesData} type={PERSONALITY} hasAnalytics={false} manager={favoritesManager} />
      <ShowPanel collections={collections} favoritesManager={favoritesManager} />
      <Bio
        name={bio?.name || seriesData?.name}
        description={bio?.shortBio || seriesData?.description}
        links={socialLinks}
        theme={Style}
      />
      <Faq
        faq={faq}
        name={bio?.name ?? ''}
        theme={{ container: classNames(Style.spacing, Style.faq) }}
      />
      <Breadcrumbs
        data={getBreadcumbsData({ params, seriesData, headline: seriesData?.headline })}
        customStyle={classNames(Style.spacing, Style.breadcrumbs)}
      />
    </main>
  );
};

export default Personality;

Personality.propTypes = {
  accessToken: PropTypes.string,
  bookmarks: PropTypes.shape({}),
  deviceId: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  params: PropTypes.shape({
    showId: PropTypes.string,
  }).isRequired,
};

Personality.fetchData = ({ store, params, accessToken }) => {
  const { showId } = params;
  return store.dispatch(fetchCategoryDetail(showId, accessToken));
};
